import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import ImageUploadModal from "../ImageUploadModal/ImageUploadModal";
import { ITrack } from "@src/types/track";
import { uploadTrackCover } from "@src/apis/trackApis";
import { useUpdateTrackMutation } from "@src/queries/trackQueries";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { useNotification } from "@src/context/NotificationContext";

export type TrackCoverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  track: ITrack | null;
  /** S3 folder the cover is uploaded to, same as the curator's audio. */
  curatorId?: string;
};

/**
 * Replaces a track's image (ROVR-4157). The file goes to S3 and its URL is
 * sent as `coverUrl`; Strapi attaches it to the track's `cover` and mirrors it
 * onto `albumCoverUrl`, so every view that reads the old field picks it up.
 * Removing sends `coverUrl: null`, which drops the override.
 */
const TrackCoverModal = ({
  isOpen,
  onClose,
  track,
  curatorId,
}: TrackCoverModalProps): React.JSX.Element => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const updateTrackMutation = useUpdateTrackMutation();

  const saveCover = async (coverUrl: string | null): Promise<void> => {
    if (!track?.documentId) return;
    await updateTrackMutation.mutateAsync({
      trackId: track.documentId,
      trackData: { coverUrl },
    });
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
    });
  };

  const handleSave = async (file: File | null): Promise<void> => {
    if (!file || !track?.documentId) return;
    addNotification("Uploading track image...", "info");
    try {
      const coverUrl = await uploadTrackCover({
        file,
        trackId: track.documentId,
        curatorId,
      });
      await saveCover(coverUrl);
      addNotification("Track image updated", "success");
    } catch {
      addNotification("Failed to update the track image", "error");
    }
  };

  const handleRemove = async (): Promise<void> => {
    try {
      await saveCover(null);
      addNotification("Custom track image removed", "success");
    } catch {
      addNotification("Failed to remove the track image", "error");
    }
  };

  return (
    <ImageUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      initialImage={track?.albumCoverUrl}
      hideCaption
      onRemove={track?.coverUrl ? handleRemove : undefined}
      removeLabel="Remove custom image"
    />
  );
};

export default TrackCoverModal;
